import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Copy, Send, Check, RotateCcw, GitBranch, Upload, Undo2, Redo2, Bookmark, Download } from "lucide-react";

const NEXT = {
  DRAFT: [{ to: "COACH_REVIEW", label: "Mandar a revisión", icon: Send, color: "#facc15" }, { to: "ARCHIVED", label: "Archivar", icon: null, color: "#a3a3a3" }],
  COACH_REVIEW: [{ to: "APPROVED", label: "Aprobar", icon: Check, color: "#4ade80" }, { to: "DRAFT", label: "Devolver a borrador", icon: RotateCcw, color: "#a3a3a3" }],
  APPROVED: [{ to: "DRAFT", label: "Reeditar (borrador)", icon: RotateCcw, color: "#a3a3a3" }],
  PUBLISHED: [],
};

export default function AdminPlanV2Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);      // for undo
  const [future, setFuture] = useState([]);        // for redo
  const [saving, setSaving] = useState(false);
  const [pubDate, setPubDate] = useState("");
  const [showPublish, setShowPublish] = useState(false);
  const [versions, setVersions] = useState([]);
  const [saveTmplModal, setSaveTmplModal] = useState(null);
  const [insertTmplModal, setInsertTmplModal] = useState(null);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = async (scope) => {
    try {
      const { data } = await api.get(`/admin/plan-templates?scope=${scope}`);
      setTemplates(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveAsTemplate = async (payload) => {
    try {
      await api.post(`/admin/plans-v2/${id}/save-as-template`, payload);
      toast.success("Plantilla creada");
      setSaveTmplModal(null);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const insertTemplate = async (tid) => {
    if (!insertTmplModal) return;
    try {
      const body = { template_id: tid };
      if (insertTmplModal.scope === "day") body.week_id = insertTmplModal.week_id;
      if (insertTmplModal.scope === "block") body.day_id = insertTmplModal.day_id;
      const { data } = await api.post(`/admin/plans-v2/${id}/insert-template`, body);
      setPlan(data);
      toast.success("Plantilla insertada");
      setInsertTmplModal(null);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const load = async () => {
    try {
      const [p, v] = await Promise.all([
        api.get(`/admin/plans-v2/${id}`),
        api.get(`/admin/plans-v2/${id}/versions`),
      ]);
      setPlan(p.data);
      setVersions(v.data.versions || []);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { load(); }, [id]);

  const snap = (next) => {
    setHistory((h) => [...h.slice(-30), plan]);
    setFuture([]);
    setPlan(next);
  };

  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [plan, ...f]);
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setPlan(prev);
  };
  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h, plan]);
    const nxt = future[0];
    setFuture((f) => f.slice(1));
    setPlan(nxt);
  };

  const save = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/plans-v2/${id}`, { name: plan.name, goal: plan.goal, coach_notes: plan.coach_notes, weeks: plan.weeks });
      setPlan(data);
      toast.success("Guardado");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const transition = async (to) => {
    try {
      const { data } = await api.post(`/admin/plans-v2/${id}/transition`, { to });
      setPlan(data);
      toast.success(`Estado: ${to}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const publish = async () => {
    if (!pubDate) return toast.error("Fecha de inicio requerida");
    try {
      const { data } = await api.post(`/admin/plans-v2/${id}/publish`, { start_date: pubDate });
      toast.success(`Publicado: ${data.count} sesiones creadas`);
      setShowPublish(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const newVersion = async () => {
    if (!window.confirm("Crear nueva versión editable a partir de esta?")) return;
    try {
      const { data } = await api.post(`/admin/plans-v2/${id}/new-version`);
      toast.success(`Versión v${data.version} creada`);
      nav(`/app/admin/plans-v2/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const rollback = async () => {
    if (!window.confirm("Deshacer publicación? Se eliminarán las sesiones creadas por este plan.")) return;
    try {
      const { data } = await api.post(`/admin/plans-v2/${id}/rollback-publish`);
      toast.success(`Rollback: ${data.removed} sesiones eliminadas`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const addWeek = () => {
    snap({ ...plan, weeks: [...(plan.weeks || []), { index: plan.weeks?.length || 0, label: `Semana ${(plan.weeks?.length || 0) + 1}`, days: [] }] });
  };

  const duplicateWeek = async (weekId) => {
    try {
      const { data } = await api.post(`/admin/plans-v2/${id}/duplicate-week/${weekId}`);
      setPlan(data);
      toast.success("Semana duplicada");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const addDay = (weekIdx) => {
    const weeks = plan.weeks.map((w, i) => i !== weekIdx ? w : { ...w, days: [...w.days, { index: w.days.length, day_type: "fuerza", title: `Día ${w.days.length + 1}`, blocks: [] }] });
    snap({ ...plan, weeks });
  };

  const removeDay = (wi, di) => {
    const weeks = plan.weeks.map((w, i) => i !== wi ? w : { ...w, days: w.days.filter((_, j) => j !== di) });
    snap({ ...plan, weeks });
  };

  const updateDay = (wi, di, field, value) => {
    const weeks = plan.weeks.map((w, i) => i !== wi ? w : {
      ...w, days: w.days.map((d, j) => j !== di ? d : { ...d, [field]: value })
    });
    snap({ ...plan, weeks });
  };

  const addBlock = (wi, di) => {
    const weeks = plan.weeks.map((w, i) => i !== wi ? w : {
      ...w, days: w.days.map((d, j) => j !== di ? d : { ...d, blocks: [...(d.blocks || []), { name: "Bloque", exercises: [] }] })
    });
    snap({ ...plan, weeks });
  };

  const addExercise = (wi, di, bi) => {
    const weeks = plan.weeks.map((w, i) => i !== wi ? w : {
      ...w, days: w.days.map((d, j) => j !== di ? d : { ...d, blocks: d.blocks.map((b, k) => k !== bi ? b : { ...b, exercises: [...(b.exercises || []), { name: "Ejercicio" }] }) })
    });
    snap({ ...plan, weeks });
  };

  const updateExercise = (wi, di, bi, ei, field, value) => {
    const weeks = plan.weeks.map((w, i) => i !== wi ? w : {
      ...w, days: w.days.map((d, j) => j !== di ? d : { ...d, blocks: d.blocks.map((b, k) => k !== bi ? b : { ...b, exercises: b.exercises.map((e, l) => l !== ei ? e : { ...e, [field]: value }) }) })
    });
    snap({ ...plan, weeks });
  };

  if (!plan) return <AppShell><div style={{ padding: 40, color: "#a3a3a3" }}>Cargando…</div></AppShell>;

  const readOnly = plan.status === "PUBLISHED" || plan.status === "ARCHIVED";

  return (
    <AppShell>
      <Link to="/app/admin/plans-v2" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#a3a3a3", fontSize: 13, textDecoration: "none", marginBottom: 12 }}>
        <ArrowLeft size={14}/> Volver a planes V2
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <input value={plan.name} onChange={(e) => snap({ ...plan, name: e.target.value })} disabled={readOnly}
                 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", background: "transparent", border: "none", color: "#fff", width: "100%", outline: "none", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "4px 0" }}
                 data-testid="plan-v2-name-edit" />
          <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 6, letterSpacing: "0.06em" }}>
            v{plan.version} · Estado <b style={{ color: "#e5e5e5" }}>{plan.status}</b>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={undo} disabled={!history.length || readOnly} style={iconBtn} title="Deshacer"><Undo2 size={14}/></button>
          <button onClick={redo} disabled={!future.length || readOnly} style={iconBtn} title="Rehacer"><Redo2 size={14}/></button>
          {!readOnly && <button onClick={save} disabled={saving} style={btnPrimary} data-testid="plan-v2-save"><Save size={14}/> {saving ? "Guardando…" : "Guardar"}</button>}
          {(NEXT[plan.status] || []).map((t) => (
            <button key={t.to} onClick={() => transition(t.to)} style={{ ...btnGhost, color: t.color, borderColor: `${t.color}44` }} data-testid={`plan-v2-transition-${t.to}`}>{t.label}</button>
          ))}
          {plan.status === "APPROVED" && <button onClick={() => setShowPublish(true)} style={{ ...btnPrimary, background: "#16a34a" }} data-testid="plan-v2-publish-btn"><Upload size={14}/> Publicar</button>}
          {plan.status === "PUBLISHED" && <button onClick={rollback} style={{ ...btnGhost, color: "#f87171", borderColor: "rgba(239,68,68,0.5)" }}>Deshacer publicación</button>}
          {(plan.status === "PUBLISHED" || plan.status === "APPROVED") && <button onClick={newVersion} style={btnGhost}><GitBranch size={14}/> Nueva versión</button>}
        </div>
      </div>

      {showPublish && (
        <div style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.3)", padding: 14, marginBottom: 20, borderRadius: 4 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>Fecha de inicio (Lunes recomendado). Se crearán {plan.weeks?.reduce((a, w) => a + (w.days?.length || 0), 0)} sesiones nuevas en el calendario del cliente.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="date" value={pubDate} onChange={(e) => setPubDate(e.target.value)} style={inputStyle} data-testid="plan-v2-pub-date" />
            <button onClick={publish} style={{ ...btnPrimary, background: "#16a34a" }} data-testid="plan-v2-confirm-publish">Confirmar publicación</button>
            <button onClick={() => setShowPublish(false)} style={btnGhost}>Cancelar</button>
          </div>
        </div>
      )}

      {versions.length > 1 && (
        <details style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 14, borderRadius: 4, marginBottom: 20 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Historial de versiones ({versions.length})</summary>
          <div style={{ marginTop: 10 }}>
            {versions.map((v) => (
              <Link key={v.id} to={`/app/admin/plans-v2/${v.id}`} style={{ display: "flex", padding: "6px 0", justifyContent: "space-between", borderBottom: "1px dashed rgba(255,255,255,0.05)", color: v.id === id ? "#dc2626" : "#e5e5e5", textDecoration: "none", fontSize: 13 }}>
                <span>v{v.version} · {v.name}</span>
                <span style={{ color: "#a3a3a3" }}>{v.status} · {new Date(v.updated_at).toLocaleDateString()}</span>
              </Link>
            ))}
          </div>
        </details>
      )}

      {plan.weeks?.map((w, wi) => (
        <div key={w.id || wi} style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 16, marginBottom: 14, borderRadius: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <input value={w.label} onChange={(e) => { const weeks = plan.weeks.map((x, i) => i !== wi ? x : { ...x, label: e.target.value }); snap({ ...plan, weeks }); }}
                   disabled={readOnly} style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 20, background: "transparent", border: "none", color: "#dc2626", outline: "none", letterSpacing: "0.12em" }} />
            {!readOnly && <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => duplicateWeek(w.id)} style={smallBtn}><Copy size={12}/> Duplicar semana</button>
              <button onClick={() => { setSaveTmplModal({ scope: "week", week_id: w.id, name: w.label }); }} style={smallBtn} data-testid={`plan-v2-save-week-tmpl-${w.id}`}><Bookmark size={12}/> Guardar como plantilla</button>
              <button onClick={() => { setInsertTmplModal({ scope: "day", week_id: w.id }); loadTemplates("day"); }} style={smallBtn}><Download size={12}/> Insertar sesión</button>
              <button onClick={() => addDay(wi)} style={smallBtn}><Plus size={12}/> Día</button>
            </div>}
          </div>
          {(w.days || []).map((d, di) => (
            <div key={d.id || di} style={{ background: "#111", border: "1px solid rgba(255,255,255,0.05)", padding: 12, marginBottom: 8, borderRadius: 3 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <input value={d.title} onChange={(e) => updateDay(wi, di, "title", e.target.value)} disabled={readOnly} placeholder="Título del día" style={{ ...inputStyle, flex: "2 1 200px" }} />
                <select value={d.day_type} onChange={(e) => updateDay(wi, di, "day_type", e.target.value)} disabled={readOnly} style={{ ...inputStyle, width: 140 }}>
                  {["fuerza","tecnica","potencia","partido","descanso","cardio","movilidad"].map((t) => <option key={t}>{t}</option>)}
                </select>
                {!readOnly && (
                  <>
                    <button onClick={() => addBlock(wi, di)} style={smallBtn}><Plus size={12}/> Bloque</button>
                    <button onClick={() => { setInsertTmplModal({ scope: "block", day_id: d.id }); loadTemplates("block"); }} style={smallBtn}><Download size={12}/> Insertar bloque</button>
                    <button onClick={() => { setSaveTmplModal({ scope: "day", day_id: d.id, name: d.title }); }} style={smallBtn} data-testid={`plan-v2-save-day-tmpl-${d.id}`}><Bookmark size={12}/> Como plantilla</button>
                    <button onClick={() => removeDay(wi, di)} style={{ ...smallBtn, color: "#f87171" }}>×</button>
                  </>
                )}
              </div>
              {(d.blocks || []).map((b, bi) => (
                <div key={b.id || bi} style={{ background: "#0a0a0a", border: "1px dashed rgba(255,255,255,0.08)", padding: 10, marginBottom: 6, borderRadius: 2 }}>
                  <input value={b.name} onChange={(e) => { const weeks = plan.weeks.map((x, i) => i !== wi ? x : { ...x, days: x.days.map((y, j) => j !== di ? y : { ...y, blocks: y.blocks.map((z, k) => k !== bi ? z : { ...z, name: e.target.value }) }) }); snap({ ...plan, weeks }); }} disabled={readOnly} placeholder="Bloque" style={{ ...inputStyle, marginBottom: 6, fontWeight: 600 }} />
                  {(b.exercises || []).map((ex, ei) => (
                    <div key={ex.id || ei} style={{ display: "grid", gridTemplateColumns: "2fr 60px 60px 70px 70px 60px 60px", gap: 4, marginBottom: 4, alignItems: "center" }}>
                      <input value={ex.name || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "name", e.target.value)} disabled={readOnly} placeholder="Ejercicio" style={inputStyleSmall} />
                      <input value={ex.sets || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "sets", e.target.value)} disabled={readOnly} placeholder="Series" style={inputStyleSmall} />
                      <input value={ex.reps || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "reps", e.target.value)} disabled={readOnly} placeholder="Reps" style={inputStyleSmall} />
                      <input value={ex.load || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "load", e.target.value)} disabled={readOnly} placeholder="Carga" style={inputStyleSmall} />
                      <input value={ex.rest || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "rest", e.target.value)} disabled={readOnly} placeholder="Desc" style={inputStyleSmall} />
                      <input value={ex.rpe || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "rpe", e.target.value)} disabled={readOnly} placeholder="RPE" style={inputStyleSmall} />
                      <input value={ex.rir || ""} onChange={(e) => updateExercise(wi, di, bi, ei, "rir", e.target.value)} disabled={readOnly} placeholder="RIR" style={inputStyleSmall} />
                    </div>
                  ))}
                  {!readOnly && <button onClick={() => addExercise(wi, di, bi)} style={{ ...smallBtn, marginTop: 4 }}><Plus size={11}/> Ejercicio</button>}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}

      {!readOnly && <button onClick={addWeek} style={{ ...btnGhost, width: "100%", justifyContent: "center", padding: 12 }} data-testid="plan-v2-add-week"><Plus size={14}/> Añadir semana</button>}

      {saveTmplModal && (
        <div style={modalBg} onClick={(e) => e.target === e.currentTarget && setSaveTmplModal(null)}>
          <div style={modalCard} data-testid="plan-v2-save-tmpl-modal">
            <h3 style={{ margin: "0 0 12px", fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase" }}>Guardar como plantilla</h3>
            <div style={{ fontSize: 12, color: "#a3a3a3", marginBottom: 10 }}>Tipo: <b style={{ color: "#e5e5e5" }}>{saveTmplModal.scope}</b></div>
            <label style={labelStyle}>Nombre *</label>
            <input value={saveTmplModal.name || ""} onChange={(e) => setSaveTmplModal({ ...saveTmplModal, name: e.target.value })} style={inputStyle} autoFocus data-testid="plan-v2-tmpl-name" />
            <label style={labelStyle}>Categoría</label>
            <input value={saveTmplModal.category || ""} onChange={(e) => setSaveTmplModal({ ...saveTmplModal, category: e.target.value })} placeholder="ej. Preseason, Fuerza básica..." style={inputStyle} />
            <label style={labelStyle}>Descripción</label>
            <textarea rows={3} value={saveTmplModal.description || ""} onChange={(e) => setSaveTmplModal({ ...saveTmplModal, description: e.target.value })} style={{ ...inputStyle, resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => saveAsTemplate(saveTmplModal)} style={btnPrimary} data-testid="plan-v2-tmpl-save">Guardar</button>
              <button onClick={() => setSaveTmplModal(null)} style={btnGhost}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {insertTmplModal && (
        <div style={modalBg} onClick={(e) => e.target === e.currentTarget && setInsertTmplModal(null)}>
          <div style={modalCard}>
            <h3 style={{ margin: "0 0 12px", fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase" }}>Insertar plantilla ({insertTmplModal.scope})</h3>
            {templates.length === 0 && <div style={{ color: "#a3a3a3", fontSize: 13, padding: 20, textAlign: "center" }}>No hay plantillas guardadas de este tipo aún.</div>}
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {templates.map((t) => (
                <button key={t.id} onClick={() => insertTemplate(t.id)}
                        style={{ display: "block", width: "100%", textAlign: "left", background: "#111", border: "1px solid rgba(255,255,255,0.1)", padding: 10, marginBottom: 6, cursor: "pointer", borderRadius: 3, color: "#fff" }}
                        data-testid={`plan-v2-tmpl-insert-${t.id}`}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                  {t.category && <div style={{ fontSize: 11, color: "#a3a3a3" }}>{t.category}</div>}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => setInsertTmplModal(null)} style={btnGhost}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const modalBg = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 };
const modalCard = { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.14)", padding: 24, borderRadius: 4, width: "100%", maxWidth: 480 };
const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginTop: 12, marginBottom: 5 };

const inputStyle = { background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 10px", fontSize: 13, borderRadius: 3, outline: "none", boxSizing: "border-box" };
const inputStyleSmall = { ...inputStyle, padding: "5px 8px", fontSize: 12 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 5, background: "#dc2626", color: "#fff", border: "none", padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 2, whiteSpace: "nowrap" };
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#e5e5e5", padding: "8px 12px", fontSize: 12, cursor: "pointer", borderRadius: 2, whiteSpace: "nowrap" };
const smallBtn = { display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#d4d4d4", padding: "4px 8px", fontSize: 11, cursor: "pointer", borderRadius: 2 };
const iconBtn = { background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#e5e5e5", padding: "8px 10px", cursor: "pointer", borderRadius: 2 };
